import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { getToken } from '../lib/auth';
import { useFormatDate } from '../lib/useSettings';
import { useVariables } from '../components/variables';
import { FieldPreview } from './FieldPreview';
import { FieldBox, PageCanvas } from './PageCanvas';
import { useEditor } from './editorStore';

export function EditorCanvas() {
  const templateId = useEditor((s) => s.templateId);
  const baseFileKey = useEditor((s) => s.baseFileKey);
  const pageSizes = useEditor((s) => s.pageSizes);
  const currentPage = useEditor((s) => s.currentPage);
  const setPage = useEditor((s) => s.setPage);
  const fields = useEditor((s) => s.fields);
  const selectedUid = useEditor((s) => s.selectedUid);
  const select = useEditor((s) => s.select);
  const updateField = useEditor((s) => s.updateField);
  const style = useEditor((s) => s.defaultTextStyle);
  const roles = useEditor((s) => s.roles);
  const { pattern: datePattern } = useFormatDate();

  // Whether a placed variable is text or a checkbox comes from the LIBRARY, not
  // from the placement — same as its label and required flag. Reading it live
  // means retyping a variable re-renders every placement immediately, instead of
  // going stale until the next save.
  const { data: variables } = useVariables();
  const variableTypes = useMemo(
    () => new Map((variables ?? []).map((v) => [v.key, v.type])),
    [variables],
  );

  const [zoom, setZoom] = useState(1);
  const token = getToken();

  // Memoize so react-pdf doesn't reload the document on every render. Keyed on
  // baseFileKey as well: a base-file swap leaves the URL identical, so without
  // it react-pdf would keep rendering the replaced PDF.
  const file = useMemo(
    () => ({ url: api.baseFileUrl(templateId), httpHeaders: { Authorization: `Bearer ${token}` } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateId, token, baseFileKey],
  );

  const onPage = fields.filter((f) => f.page === currentPage);

  return (
    <PageCanvas
      file={file}
      page={currentPage}
      pageCount={pageSizes.length}
      onPageChange={setPage}
      zoom={zoom}
      onZoomChange={setZoom}
      size={pageSizes[currentPage - 1]}
      onDeselect={() => select(null)}
    >
      {onPage.map((f) => (
        <FieldBox
          key={f.uid}
          field={f}
          zoom={zoom}
          selected={selectedUid === f.uid}
          onSelect={() => select(f.uid)}
          onChange={(geometry) => updateField(f.uid, geometry)}
        >
          <FieldPreview
            field={f}
            zoom={zoom}
            style={style}
            datePattern={datePattern}
            roleLabel={roles.find((r) => r.key === f.signerRole)?.label ?? f.signerRole ?? ''}
            variableType={f.key ? variableTypes.get(f.key) : undefined}
          />
        </FieldBox>
      ))}
    </PageCanvas>
  );
}
